import React, { useEffect, useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Brain, CheckCircle2, Lock, Sparkles, Zap } from 'lucide-react';
interface SkillNode {
  id: string;
  label: string;
  x: number;
  y: number;
  group: 'beheerst' | 'actief' | 'ontgrendeld' | 'gesloten';
  prereqs: string[];
}
const NODES: SkillNode[] = [{
  id: 'a',
  label: 'Tafels',
  x: 12,
  y: 50,
  group: 'beheerst',
  prereqs: []
}, {
  id: 'b',
  label: 'Schatten',
  x: 30,
  y: 25,
  group: 'beheerst',
  prereqs: ['a']
}, {
  id: 'c',
  label: 'Breuken lezen',
  x: 30,
  y: 75,
  group: 'beheerst',
  prereqs: ['a']
}, {
  id: 'd',
  label: 'Procenten',
  x: 52,
  y: 25,
  group: 'actief',
  prereqs: ['b']
}, {
  id: 'e',
  label: 'Breuken +',
  x: 52,
  y: 75,
  group: 'actief',
  prereqs: ['c']
}, {
  id: 'f',
  label: 'Verhoudingen',
  x: 74,
  y: 50,
  group: 'ontgrendeld',
  prereqs: ['d', 'e']
}, {
  id: 'g',
  label: 'Schaal',
  x: 92,
  y: 30,
  group: 'gesloten',
  prereqs: ['f']
}, {
  id: 'h',
  label: 'Wortels',
  x: 92,
  y: 70,
  group: 'gesloten',
  prereqs: ['f']
}];
const STYLE = {
  beheerst: {
    fill: '#10B981',
    stroke: '#047857',
    ring: 'ring-emerald-200',
    text: 'text-emerald-700',
    bg: 'bg-emerald-50',
    icon: CheckCircle2
  },
  actief: {
    fill: '#F97316',
    stroke: '#C2410C',
    ring: 'ring-orange-200',
    text: 'text-orange-700',
    bg: 'bg-orange-50',
    icon: Zap
  },
  ontgrendeld: {
    fill: '#FBBF24',
    stroke: '#D97706',
    ring: 'ring-amber-200',
    text: 'text-amber-700',
    bg: 'bg-amber-50',
    icon: Sparkles
  },
  gesloten: {
    fill: '#CBD5E1',
    stroke: '#94A3B8',
    ring: 'ring-slate-200',
    text: 'text-slate-500',
    bg: 'bg-slate-50',
    icon: Lock
  }
};
export function AdaptiveFlowSection() {
  const [pulseStep, setPulseStep] = useState(0);
  useEffect(() => {
    const t = setInterval(() => setPulseStep(s => (s + 1) % 3), 2400);
    return () => clearInterval(t);
  }, []);
  return <section id="adaptief" className="relative py-24 lg:py-32 bg-[#FFF9F2] overflow-hidden" data-id="element-1">
      <div className="absolute inset-0 pointer-events-none" data-id="element-2">
        <div className="absolute top-0 right-0 w-[40rem] h-[40rem] bg-violet-200/15 rounded-full blur-3xl" data-id="element-3" />
      </div>

      <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-4">
        <div className="grid lg:grid-cols-2 gap-12 lg:gap-20 items-center" data-id="element-5">
          {/* Left: Copy */}
          <div data-id="element-6">
            <motion.div initial={{
            opacity: 0,
            x: -20
          }} whileInView={{
            opacity: 1,
            x: 0
          }} viewport={{
            once: true
          }} className="inline-flex items-center gap-2 bg-violet-100 text-violet-700 px-3 py-1.5 rounded-full text-xs font-black uppercase tracking-wider mb-6" data-id="element-7">
              <Brain className="w-3.5 h-3.5" data-id="element-8" />
              De leerkaart
            </motion.div>

            <h2 className="text-4xl md:text-5xl lg:text-6xl font-black text-slate-900 mb-6 tracking-tight leading-[1.05]" data-id="element-9">
              Geen rijtje van A naar Z.{' '}
              <span className="text-transparent bg-clip-text bg-gradient-to-r from-violet-600 to-fuchsia-500" data-id="element-10">
                Een routekaart die meegroeit.
              </span>
            </h2>

            <p className="text-lg text-slate-600 mb-8 leading-relaxed font-medium" data-id="element-11">
              Elke vaardigheid bouwt op een eerdere. Zit{' '}
              <strong className="text-slate-800" data-id="element-12">tafels</strong> goed? Dan komen{' '}
              <strong className="text-slate-800" data-id="element-13">schatten</strong> en{' '}
              <strong className="text-slate-800" data-id="element-14">breuken lezen</strong> in
              beeld. Pas daarna{' '}
              <strong className="text-slate-800" data-id="element-15">procenten</strong>. Meester
              djay kiest steeds de volgende stap die past — net moeilijk genoeg
              om te groeien, nooit zo moeilijk dat je kind vastloopt.
            </p>

            <div className="space-y-3" data-id="element-16">
              {[{
              icon: CheckCircle2,
              color: 'text-emerald-500',
              text: 'Beheerst: zit goed — basis voor de volgende stap'
            }, {
              icon: Zap,
              color: 'text-orange-500',
              text: 'Bezig: hier wordt nu aan gewerkt'
            }, {
              icon: Sparkles,
              color: 'text-amber-500',
              text: 'Net open: precies de juiste uitdaging'
            }, {
              icon: Lock,
              color: 'text-slate-400',
              text: 'Nog dicht: eerst de basis stevig krijgen'
            }].map((row, i) => <motion.div key={i} initial={{
              opacity: 0,
              x: -10
            }} whileInView={{
              opacity: 1,
              x: 0
            }} viewport={{
              once: true
            }} transition={{
              delay: i * 0.08
            }} className="flex items-center gap-3" data-id="element-17">
                  <row.icon className={`w-5 h-5 ${row.color} shrink-0`} data-id="element-18" />
                  <span className="text-sm text-slate-700 font-bold" data-id="element-19">
                    {row.text}
                  </span>
                </motion.div>)}
            </div>
          </div>

          {/* Right: Animated LDG */}
          <motion.div initial={{
          opacity: 0,
          scale: 0.95
        }} whileInView={{
          opacity: 1,
          scale: 1
        }} viewport={{
          once: true
        }} transition={{
          duration: 0.6
        }} className="relative" data-id="element-20">
            <div className="relative bg-white rounded-[2rem] border-4 border-white shadow-2xl p-6 overflow-hidden" data-id="element-21">
              {/* Header */}
              <div className="flex items-center justify-between mb-4" data-id="element-22">
                <div className="text-xs font-black text-slate-800" data-id="element-23">
                  Rekenen · Groep 7
                </div>
                <div className="flex items-center gap-1.5 text-[10px] font-bold text-violet-600 bg-violet-50 px-2 py-1 rounded-full" data-id="element-24">
                  <motion.div animate={{
                  scale: [1, 1.4, 1]
                }} transition={{
                  duration: 1.5,
                  repeat: Infinity
                }} className="w-1.5 h-1.5 rounded-full bg-violet-500" data-id="element-25" />
                  LIVE
                </div>
              </div>

              <div className="relative aspect-[4/3] bg-gradient-to-br from-slate-50 to-white rounded-2xl border border-slate-100 p-2" data-id="element-26">
                <svg viewBox="0 0 100 100" className="absolute inset-0 w-full h-full" preserveAspectRatio="none" data-id="element-27">
                  {/* Edges */}
                  {NODES.flatMap(node => node.prereqs.map(prereqId => {
                  const from = NODES.find(n => n.id === prereqId)!;
                  const isActiveEdge = node.group === 'actief' || node.group === 'ontgrendeld';
                  return <g key={`${prereqId}-${node.id}`} data-id="element-28">
                          <line x1={from.x} y1={from.y} x2={node.x} y2={node.y} stroke={isActiveEdge ? '#A78BFA' : '#E2E8F0'} strokeWidth="0.5" strokeDasharray={node.group === 'gesloten' ? '1 1' : 'none'} data-id="element-29" />
                          {isActiveEdge && <motion.circle r="1" fill="#8B5CF6" initial={{
                      cx: from.x,
                      cy: from.y,
                      opacity: 0
                    }} animate={{
                      cx: [from.x, node.x],
                      cy: [from.y, node.y],
                      opacity: [0, 1, 0]
                    }} transition={{
                      duration: 2,
                      repeat: Infinity,
                      delay: (from.id.charCodeAt(0) + node.id.charCodeAt(0)) % 3,
                      ease: 'easeInOut'
                    }} data-id="element-30" />}
                        </g>;
                }))}
                </svg>

                {/* Nodes */}
                {NODES.map((node, idx) => {
                const s = STYLE[node.group];
                const Icon = s.icon;
                const isActive = node.group === 'actief';
                return <motion.div key={node.id} className="absolute -translate-x-1/2 -translate-y-1/2" style={{
                  left: `${node.x}%`,
                  top: `${node.y}%`
                }} initial={{
                  opacity: 0,
                  scale: 0
                }} whileInView={{
                  opacity: 1,
                  scale: 1
                }} viewport={{
                  once: true
                }} transition={{
                  delay: 0.3 + idx * 0.08,
                  type: 'spring',
                  stiffness: 200
                }} data-id="element-31">
                      <motion.div className={`relative flex flex-col items-center`} animate={isActive ? {
                    y: [0, -3, 0]
                  } : node.group === 'ontgrendeld' ? {
                    scale: [1, 1.05, 1]
                  } : undefined} transition={{
                    duration: 2,
                    repeat: Infinity,
                    ease: 'easeInOut'
                  }} data-id="element-32">
                        {isActive && <motion.div className="absolute inset-0 rounded-full bg-orange-400" animate={{
                      scale: [1, 1.8],
                      opacity: [0.4, 0]
                    }} transition={{
                      duration: 1.5,
                      repeat: Infinity
                    }} style={{
                      width: '32px',
                      height: '32px',
                      left: '-2px',
                      top: '-2px'
                    }} data-id="element-33" />}
                        <div className={`relative w-7 h-7 rounded-full ${s.bg} ring-4 ${s.ring} flex items-center justify-center shadow-sm`} style={{
                      borderColor: s.stroke
                    }} data-id="element-34">
                          <Icon className={`w-3.5 h-3.5 ${s.text}`} strokeWidth={3} data-id="element-35" />
                        </div>
                        <div className={`absolute top-9 whitespace-nowrap text-[9px] font-black ${s.text} bg-white/90 px-1.5 py-0.5 rounded-md shadow-sm`} data-id="element-36">
                          {node.label}
                        </div>
                      </motion.div>
                    </motion.div>;
              })}
              </div>

              {/* Footer ticker */}
              <div className="mt-4 bg-slate-900 rounded-xl p-3 text-white" data-id="element-37">
                <AnimatePresence mode="wait" data-id="element-38">
                  <motion.div key={pulseStep} initial={{
                  opacity: 0,
                  y: 8
                }} animate={{
                  opacity: 1,
                  y: 0
                }} exit={{
                  opacity: 0,
                  y: -8
                }} transition={{
                  duration: 0.3
                }} className="flex items-center gap-2 text-[11px] font-bold" data-id="element-39">
                    {pulseStep === 0 && <>
                        <Brain className="w-3.5 h-3.5 text-violet-400" data-id="element-40" />
                        <span className="text-slate-300" data-id="element-41">
                          Volgende vraag wordt gekozen…
                        </span>
                      </>}
                    {pulseStep === 1 && <>
                        <CheckCircle2 className="w-3.5 h-3.5 text-emerald-400" data-id="element-42" />
                        <span className="text-slate-300" data-id="element-43">
                          <strong className="text-emerald-300" data-id="element-44">
                            Procenten
                          </strong>{' '}
                          zit goed — 82% raak
                        </span>
                      </>}
                    {pulseStep === 2 && <>
                        <Sparkles className="w-3.5 h-3.5 text-amber-400" data-id="element-45" />
                        <span className="text-slate-300" data-id="element-46">
                          <strong className="text-amber-300" data-id="element-47">
                            Verhoudingen
                          </strong>{' '}
                          staat open — tijd voor de volgende stap
                        </span>
                      </>}
                  </motion.div>
                </AnimatePresence>
              </div>
            </div>
          </motion.div>
        </div>
      </div>
    </section>;
}